import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AI_WELCOME_SUGGESTIONS, AiCapabilityCategory, AiGreetingDay, aiSuggestionIcon, AiSuggestion } from '../../models/ai-assistant.models';

/**
 * Welcome / zero-state shown when the thread is empty: a short intro, tappable suggestion
 * chips that send immediately (FRONTEND_HANDOFF §3), and a collapsible "What can I do?"
 * capability panel populated from `GET /config` (§8). Capability examples are sent directly.
 */
@Component({
  selector: 'app-ai-assistant-empty-state',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ai-assistant-empty-state.component.html',
  styleUrls: ['./ai-assistant-empty-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantEmptyStateComponent {
  @Input() suggestions: AiSuggestion[] = AI_WELCOME_SUGGESTIONS;
  @Input() capabilities: AiCapabilityCategory[] = [];
  /** Personalized greeting line, e.g. "Good morning, Rosina." Shown above the offer. */
  @Input() greeting = '';
  /** The offer shown after the greeting, e.g. "Would you like me to run through your day?". */
  @Input() prompt = 'Would you like me to run through your day?';
  /** Quick live counts for the day badge (from `GET /greeting`); null hides the badge. */
  @Input() day: AiGreetingDay | null = null;
  /** True while the user is typing in the composer — hides the helper paragraph to declutter. */
  @Input() typing = false;

  /** Send a prompt immediately (suggestion chips / capability examples / "What can I do?"). */
  @Output() ask = new EventEmitter<string>();

  showCapabilities = false;

  /** Map a backend icon hint (e.g. `user-plus`) to a PrimeIcons class. */
  iconClass(icon?: string): string {
    return aiSuggestionIcon(icon);
  }

  toggleCapabilities(): void {
    this.showCapabilities = !this.showCapabilities;
    // No data yet → let the assistant answer in-thread instead of an empty panel.
    if (this.showCapabilities && !this.capabilities.length) {
      this.ask.emit('What can I do?');
    }
  }
}
